import { useTranslation } from 'react-i18next';
import {
	Typography,
	Card,
	CardContent,
	Stack,
	Box,
	IconButton,
	Button,
} from '@mui/material';
import { DefaultValues, FormProvider, useForm } from 'react-hook-form';
import { useCallback, useEffect, useState } from 'react';
import AppDataGrid, {
	initialPagination,
} from '@/common/components/dataGrid/AppDataGrid';
import StaffListSummaryFilterSection from './components/NotificationListSummaryFilterSection';
import { useSearchParams } from 'react-router-dom';
import { GridColDef } from '@mui/x-data-grid';
import qs from 'qs';
import { filterObjectByKeys, parseSummarySearchParams } from '@/utils/qsHelper';
import { DISPLAY_DATETIME_12H_FORMAT } from '@/utils/dateHelper';
import dayjs from 'dayjs';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import { FormType } from '@/api/enum/formType.enum';
import ConfirmationDialog from '@/common/components/ConfirmationDialog/ConfirmationDialog';
import useModal from '@/common/components/ConfirmationDialog/useModal';
import { ClickPageDTO } from '@/api/DTO/app-setting/clock.interface';
import { closeSnackbar, enqueueSnackbar } from 'notistack';
import CloseIcon from '@mui/icons-material/Close';
import NoticeDialog from './components/NoticeDialog';
import ModeOutlinedIcon from '@mui/icons-material/ModeOutlined';
import {
	useDeleteNoticeMutation,
	useGetNoticeListQuery,
} from '@/api/app-setting/noticeApiSlice';
import {
	NoticePageDTO,
	NoticePageFilter,
} from '@/api/DTO/app-setting/notice.interface';
import NoteAltOutlinedIcon from '@mui/icons-material/NoteAltOutlined';

export interface DataGrid {
	updatedAt: string;
	createdAt: string;
}

export default function NotificationListSummaryPage() {
	const { t } = useTranslation();
	const [searchParams, setSearchParams] = useSearchParams();
	const [isInitializedForm, setIsInitializedForm] = useState<boolean>(false);
	const showModal = useModal();
	const [deleteNotice] = useDeleteNoticeMutation();
	const [reSetOpen, setReSetOpen] = useState(false);
	const [row, setRow] = useState({});
	const [type, setType] = useState<FormType>(FormType.edit);

	// useGetUser返回 userInfo 和 userRoles
	// 接收一个对象
	// 第一个入参shouldRefetch：是否再次调用获取用户信息api
	// 第二个入参roleObj：传入需要判断的权限返回权限对象boolean
	// roleObj的key自定义，值为需要判断的权限，返回自定义key和对应的boolean
	// const { userRoles } = useGetUser<useGetUserResultType>({
	// 	shouldRefetch: true,
	// 	roleObj: {
	// 		create: ['create', 'staffList'],
	// 	},
	// });

	const defaultPropertyFilter: NoticePageFilter = {
		pagination: initialPagination,
		searchString: '',
		sortings: [],
	};
	const defaultValues: DefaultValues<NoticePageFilter> = defaultPropertyFilter;
	const methods = useForm<NoticePageFilter>({
		defaultValues,
	});
	const transformedSearchParams: NoticePageFilter = {
		...defaultPropertyFilter,
		...filterObjectByKeys(parseSummarySearchParams(searchParams), [
			'pagination',
			'sortings',
			'searchString',
		]),
	};

	const { data: propertySummary, isSuccess: isGetPropertySummarySuccess } =
		useGetNoticeListQuery(transformedSearchParams, {
			refetchOnMountOrArgChange: true,
		});

	const transformResponse = useCallback((response?: NoticePageDTO[]) => {
		if (response == null) return;
		const newResponse: DataGrid[] = [];
		response.forEach((item) => {
			newResponse.push({
				...item,
				updatedAt: !item.updatedAt
					? '-'
					: dayjs(item.updatedAt)
							.locale('en')
							.format(DISPLAY_DATETIME_12H_FORMAT),
				createdAt: !item.createdAt
					? '-'
					: dayjs(item.createdAt)
							.locale('en')
							.format(DISPLAY_DATETIME_12H_FORMAT),
			});
		});
		return newResponse;
	}, []);

	// watch fields change
	useEffect(() => {
		const subscription = methods.watch((value) => {
			const propertyFilterFormResult = value as NoticePageFilter;

			// append to url to reload page
			setSearchParams(qs.stringify(propertyFilterFormResult));
		});
		return () => subscription.unsubscribe();
	}, [methods.watch]);

	// watch serach params
	useEffect(() => {
		if (!isInitializedForm) {
			methods.reset(transformedSearchParams, {
				keepDefaultValues: true,
			});
			setIsInitializedForm(true);
		}
	}, [searchParams]);

	const columns: GridColDef[] = [
		{
			field: 'sort',
			headerName: t('notice.index'),
			flex: 5,
		},
		{
			field: 'contentZh',
			headerName: t('notice.noticeTemplateZh'),
			flex: 21,
		},
		{
			field: 'contentEn',
			headerName: t('notice.noticeTemplateEn'),
			flex: 21,
		},
		{
			field: 'updatedAt',
			headerName: t('notice.lastUpdateTime'),
			flex: 21,
		},
		{
			field: 'createdAt',
			headerName: t('notice.createTime'),
			flex: 21,
		},
		{
			field: 'edit',
			headerName: '',
			width: 68,
			renderCell: (params) => (
				<IconButton
					color='warning'
					onClick={() => {
						setRow(params.row);
						setReSetOpen(true);
						setType(FormType.edit);
					}}
				>
					<ModeOutlinedIcon />
				</IconButton>
			),
		},
		{
			field: 'delete',
			headerName: '',
			width: 68,
			renderCell: (params) => (
				<IconButton color='error' onClick={() => handleDelete(params.row)}>
					<DeleteOutlinedIcon />
				</IconButton>
			),
		},
	];

	const handleDelete = async (obj: ClickPageDTO) => {
		showModal((props: any) => (
			<ConfirmationDialog
				{...props}
				showCloseIcon={true}
				title={t('notice.deleteTemplate')}
				titleProps={{ variant: 'h6' }}
				description={
					<Typography variant='body1'>
						{t('notice.isDeleteTemplate')}
					</Typography>
				}
				cancellationText={t('button.cancel')}
				confirmationText={t('button.isConfirm')}
				confirmationButtonProps={{ type: 'submit', variant: 'contained' }}
				onConfirm={async () => {
					const response = await deleteNotice(obj.id || '');
					if ('data' in response) {
						enqueueSnackbar(t('snackbar.deleteNoticeSuccess'), {
							variant: 'success',
							anchorOrigin: { horizontal: 'center', vertical: 'top' },
							autoHideDuration: 3000,
							action: (key) => {
								return <CloseIcon onClick={() => closeSnackbar(key)} />;
							},
						});
					}
				}}
			/>
		));
	};

	return (
		<>
			<Box display='flex' justifyContent='space-between'>
				<Typography variant='h4'>
					{t('drawerListItems.notificationList')}
				</Typography>
				<Stack direction={'row'} alignItems={'center'} spacing={3}>
					<Button
						variant='contained'
						endIcon={<NoteAltOutlinedIcon />}
						onClick={() => {
							setReSetOpen(true);
							setType(FormType.create);
						}}
						style={{ alignItems: 'center' }}
					>
						{t('notice.createTemplate')}
					</Button>
				</Stack>
			</Box>

			<Card sx={{ marginTop: 3 }}>
				<CardContent>
					<FormProvider {...methods}>
						<StaffListSummaryFilterSection></StaffListSummaryFilterSection>

						<Box marginTop={2}>
							<AppDataGrid
								loading={!isGetPropertySummarySuccess}
								rows={transformResponse(propertySummary?.data?.items) ?? []}
								rowCount={propertySummary?.data?.pagination.totalItems ?? 0}
								columns={columns}
								getRowId={(row) => row.id}
							/>
						</Box>
					</FormProvider>
				</CardContent>
			</Card>
			<NoticeDialog
				reSetOpen={reSetOpen}
				setReSetOpen={setReSetOpen}
				type={type}
				setType={setType}
				row={row}
			/>
		</>
	);
}
